import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import { IDelegateRole } from "../interfaces/IDelegateRole";
import { DelegateRoleService } from "../services/delegateRoleService";
import { RootState } from "./store";

// Selectors
export const selectDelegateRolesLoading = (state: RootState) => state.delegateRoles.loading;
export const selectDelegateRoles = (state: RootState) => state.delegateRoles.delegateRoles;

// Async actions
export const fetchDelegateRoles = createAsyncThunk<IDelegateRole[], AxiosInstance, { state: RootState }>(
    "delegateRoles/fetch",
    async (apiClient, { getState }) => {
        const { delegateRoles, lastFetched } = getState().delegateRoles;
        if (delegateRoles.length > 0 && Date.now() - lastFetched < 5 * 60 * 1000) {
            return delegateRoles;
        }
        const delegateRoleService = new DelegateRoleService(apiClient);
        return await delegateRoleService.getAll<IDelegateRole[]>();
    },
    {
        condition: (_apiClient, { getState }) => {
            const { delegateRoles, lastFetched } = getState().delegateRoles;
            return !(delegateRoles.length > 0 && Date.now() - lastFetched < 5 * 60 * 1000);
        }
    }
);

export const addDelegateRole = createAsyncThunk<IDelegateRole, { delegateRole: IDelegateRole; apiClient: AxiosInstance }>(
    "delegateRoles/add",
    async ({ delegateRole, apiClient }) => {
        const delegateRoleService = new DelegateRoleService(apiClient);
        return await delegateRoleService.create(delegateRole);
    }
);

export const updateDelegateRole = createAsyncThunk<IDelegateRole, { delegateRoleId: string; updatedDelegateRole: IDelegateRole; apiClient: AxiosInstance }>(
    "delegateRoles/update",
    async ({ delegateRoleId, updatedDelegateRole, apiClient }) => {
        const delegateRoleService = new DelegateRoleService(apiClient);
        return await delegateRoleService.update(delegateRoleId, updatedDelegateRole);
    }
);

export const deleteDelegateRole = createAsyncThunk<string, { delegateRoleId: string; apiClient: AxiosInstance }>(
    "delegateRoles/delete",
    async ({ delegateRoleId, apiClient }) => {
        const delegateRoleService = new DelegateRoleService(apiClient);
        await delegateRoleService.delete(delegateRoleId);
        return delegateRoleId;
    }
);

const delegateRolesSlice = createSlice({
    name: "delegateRoles",
    initialState: {
        delegateRoles: [] as IDelegateRole[],
        loading: false,
        lastFetched: 0
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchDelegateRoles.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchDelegateRoles.fulfilled, (state, action) => {
                state.loading = false;
                state.delegateRoles = action.payload;
                state.lastFetched = Date.now();
            })
            .addCase(addDelegateRole.fulfilled, (state, action) => {
                state.delegateRoles.push(action.payload);
            })
            .addCase(updateDelegateRole.fulfilled, (state, action) => {
                state.delegateRoles = state.delegateRoles.map(role =>
                    role.id === action.payload.id ? action.payload : role
                );
            })
            .addCase(deleteDelegateRole.fulfilled, (state, action) => {
                state.delegateRoles = state.delegateRoles.filter(role => role.id !== action.payload);
            });
    }
});

export default delegateRolesSlice.reducer;
